import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ScriptMetaEditor } from "@/components/case/script-meta-editor";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Field, FieldLabel } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import type { CaseDetail, CaseVersion } from "@/lib/api";
import { cn } from "@/lib/utils";

const selectClassName = cn(
  "h-8 w-full min-w-[12rem] max-w-xs rounded-md border border-input bg-input/20 px-2 py-0.5 text-sm outline-none",
  "focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30",
  "disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
);

function versionOptionLabel(v: CaseVersion): string {
  const branch = v.branchLabel ? ` · ${v.branchLabel}` : "";
  return `${v.locale} v${v.versionN} · ${v.status}${branch}`;
}

function pickDefaultVersion(versions: CaseVersion[]): CaseVersion | null {
  if (versions.length === 0) return null;
  return [...versions].sort((a, b) => {
    if (b.versionN !== a.versionN) return b.versionN - a.versionN;
    return b.updatedAt.localeCompare(a.updatedAt);
  })[0]!;
}

export function CaseSourcePanel({
  caseId,
  detail,
}: {
  caseId: string;
  detail: CaseDetail;
}) {
  const versions = detail.versions;
  const defaultVersion = useMemo(
    () => pickDefaultVersion(versions),
    [versions],
  );
  const [versionId, setVersionId] = useState(defaultVersion?.id ?? "");

  useEffect(() => {
    if (!versionId && defaultVersion) {
      setVersionId(defaultVersion.id);
      return;
    }
    if (versionId && !versions.some((v) => v.id === versionId)) {
      setVersionId(defaultVersion?.id ?? "");
    }
  }, [versionId, versions, defaultVersion]);

  const version = versions.find((v) => v.id === versionId) ?? defaultVersion;

  if (versions.length === 0 || !version) {
    return (
      <Alert>
        <AlertTitle>尚无版本</AlertTitle>
        <AlertDescription>
          请先创建版本。剧本与元信息按版本存储。
        </AlertDescription>
      </Alert>
    );
  }

  if (!versionId) {
    return <Skeleton className="h-64 w-full" />;
  }

  const versionItems = versions.map((v) => v.id);

  return (
    <ScriptMetaEditor
      caseId={caseId}
      versionId={version.id}
      caseDisplayId={detail.case.id}
      versionStatus={version.status}
      hasPipeline={version.hasPipeline ?? false}
      role="source"
      title="源文"
      description="案件输入库：原始剧本与元信息。可随时编辑保存。切换版本以对比包内容。在该版本的「元信息」页启动流水线。"
      autoDraft={false}
      showStartPipeline={false}
      crossLink={
        <>
          与版本「元信息」字段相同。{" "}
          <Link
            to="/cases/$caseId/versions/$versionId/meta"
            params={{ caseId, versionId: version.id }}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            打开 {version.locale} v{version.versionN} 的元信息
          </Link>{" "}
          以审核并启动流水线。
        </>
      }
      headerExtra={
        versions.length > 1 ? (
          <Field className="min-w-[12rem] max-w-xs gap-1">
            <FieldLabel className="sr-only">版本</FieldLabel>
            <Combobox
              items={versionItems}
              value={version.id}
              onValueChange={(next) => {
                if (typeof next === "string") setVersionId(next);
              }}
            >
              <ComboboxInput
                className={selectClassName}
                showClear={false}
                placeholder="版本"
              />
              <ComboboxContent>
                <ComboboxEmpty>暂无版本</ComboboxEmpty>
                <ComboboxList>
                  {(id) => {
                    const item = versions.find((v) => v.id === id);
                    return (
                      <ComboboxItem key={id} value={id}>
                        {item ? versionOptionLabel(item) : id}
                      </ComboboxItem>
                    );
                  }}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
        ) : null
      }
    />
  );
}
